import React from 'react';
import PageTransition from '../components/ui/PageTransition';
import { motion } from 'framer-motion';

const TermsPage: React.FC = () => {
  return (
    <PageTransition>
      <section className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h1 
              className="text-4xl font-bold text-gray-900"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Terms of Service
            </motion.h1>
            <motion.p 
              className="mt-4 text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Last updated: April 19, 2025
            </motion.p>
          </div>
          
          <div className="prose prose-lg mx-auto text-gray-800">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <h2>1. Introduction</h2>
              <p>
                Welcome to AgentSpot. These Terms of Service ("Terms") govern your access to and use of the AgentSpot platform, including any associated websites, APIs, and services (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
              </p>

              <h2>2. Definitions</h2>
              <p>
                <strong>"AgentSpot"</strong> refers to our company, including our directors, officers, employees, and affiliates.<br />
                <strong>"User"</strong> refers to any individual who accesses or uses the Service.<br />
                <strong>"Content"</strong> refers to any information, data, text, software, graphics, or other materials that may be viewed or accessed through the Service.
              </p>

              <h2>3. Account Registration</h2>
              <p>
                To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </p>

              <h2>4. Your Responsibilities</h2>
              <p>
                You are responsible for safeguarding your account credentials and for any activities or actions under your account. You agree not to share your account credentials with any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
              </p>

              <h2>5. Acceptable Use</h2>
              <p>
                You agree not to use the Service for any unlawful purpose or in any way that could damage, disable, overburden, or impair the Service. You agree not to attempt to gain unauthorized access to any part of the Service, or any system or network connected to the Service.
              </p>

              <h2>6. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are and will remain the exclusive property of AgentSpot and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
              </p>

              <h2>7. User Content</h2>
              <p>
                You retain all rights to any content you submit, post, or display on or through the Service. By submitting, posting, or displaying content on or through the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, modify, and display the content in connection with providing the Service.
              </p>

              <h2>8. Privacy</h2>
              <p>
                Our <a href="/privacy" className="text-primary-600 hover:underline">Privacy Policy</a> describes how we collect, use, and share information about you when you use our Service. By using the Service, you agree to the collection, use, and sharing of your information as described in our Privacy Policy.
              </p>

              <h2>9. Termination</h2>
              <p>
                We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms.
              </p>

              <h2>10. Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on this page and updating the "Last updated" date at the top of these Terms.
              </p>

              <h2>11. Limitation of Liability</h2>
              <p>
                In no event shall AgentSpot be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
              </p>

              <h2>12. Governing Law</h2>
              <p>
                These Terms shall be governed and construed in accordance with the laws of [Jurisdiction], without regard to its conflict of law provisions.
              </p>

              <h2>13. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us at support@agentspot.ai.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
};

export default TermsPage;